import { LogOut } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from "framer-motion";

const BOARD_MAP = {
  cbse: 'CBSE', ts_ssc: 'TS SSC', ap_ssc: 'AP SSC', icse: 'ICSE', ib: 'IB',
  'CBSE/NCERT': 'CBSE', 'Telangana SSC': 'TS SSC', 'AP SSC': 'AP SSC', 'ICSE': 'ICSE',
  'International Baccalaureate (IB)': 'IB',
};


export const Header = ({ onHowItWorks, streakCount = 0, masteryData = [], onShowAuth, onLogoClick }) => {
  const { user, signOut } = useAuth();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const boardRaw    = localStorage.getItem('you_board') || '';
  const totalXP     = parseInt(localStorage.getItem('you_total_xp') || '0');
  const boardDisplay = BOARD_MAP[boardRaw] || boardRaw;

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 z-50"
      style={{ background: 'rgba(255,255,255,0.85)', borderBottom: '2px dashed #DDDDDD', width: '100%', maxWidth: 720, left: 0, right: 0, margin: '0 auto' }}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between header-bar" style={{ height: 56, padding: '0 12px' }}>

        {/* ── Left: Logo ── */}
        <button
          onClick={onLogoClick || scrollToTop}
          aria-label="Go to home"
          className="outline-none z-50 shrink-0 hover:opacity-90 transition-opacity"
          data-testid="app-logo"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo-book.png" alt="YOU" style={{ width: 36, height: 36, objectFit: 'contain' }} />
            <div className="header-logo-text">
              <img src="/logo-you-text.png" alt="YOU" style={{ height: 16, objectFit: 'contain', display: 'block' }} />
              <img src="/logo-subtitle.png" alt="Your Own Understanding" className="header-subtitle" style={{ height: 9, objectFit: 'contain', display: 'block', marginTop: 2, opacity: 0.7 }} />
            </div>
          </div>
        </button>

        {/* ── Right: compact stats + avatar ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

          {/* Board badge — only after onboarding */}
          {boardRaw && (
            <span className="header-board-badge" style={{
              fontSize: 10, color: '#448AFF', fontWeight: 400,
              fontFamily: "'Patrick Hand', cursive",
              background: 'rgba(68,138,255,0.1)',
              padding: '2px 6px', borderRadius: 4,
              border: '1px solid rgba(68,138,255,0.2)',
              whiteSpace: 'nowrap',
            }}>
              {boardDisplay}
            </span>
          )}

          {/* Streak + XP */}
          <span className="header-stats" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ fontSize: 14 }}>🔥</span>
            <span style={{ fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: streakCount > 0 ? '#D32F2F' : '#999999', fontSize: 13 }}>{streakCount > 0 ? streakCount : '0'}</span>
            <span style={{ fontSize: 14, marginLeft: 4 }}>⭐</span>
            <span style={{ fontWeight: 400, fontFamily: "'Patrick Hand', cursive", color: totalXP > 0 ? '#E6A800' : '#999999', fontSize: 13 }}>{totalXP > 0 ? totalXP : '0'}</span>
          </span>

          {/* Avatar (with sign-out dropdown) or compact Sign in */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: '#448AFF', border: '1.5px dashed #448AFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, color: '#fff', fontWeight: 400,
                    cursor: 'pointer', flexShrink: 0, overflow: 'hidden',
                  }}
                  role="button"
                  aria-label="Open profile menu"
                  data-testid="user-menu-btn"
                >
                  {(() => {
                    const customAvatar = localStorage.getItem('you_custom_avatar');
                    const googleAvatar = user.user_metadata?.avatar_url;
                    const avatarSrc = customAvatar || googleAvatar;
                    return avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt="Profile photo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }}
                      />
                    ) : null;
                  })()}
                  <span style={{
                    display: (localStorage.getItem('you_custom_avatar') || user.user_metadata?.avatar_url) ? 'none' : 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    width: '100%', height: '100%', fontSize: 11,
                  }}>
                    {(user.user_metadata?.full_name || user.email || '?')[0].toUpperCase()}
                  </span>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40" data-testid="user-dropdown">
                <DropdownMenuItem onSelect={() => signOut()} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => onShowAuth?.()}
              aria-label="Sign in"
              className="header-signin-btn"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                cursor: 'pointer', padding: '4px 8px',
                borderRadius: 12, border: '1px dashed rgba(68,138,255,0.3)',
                background: 'rgba(68,138,255,0.08)',
                minWidth: 36, minHeight: 36,
              }}
              data-testid="google-signin-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="header-signin-text" style={{ fontSize: 11, color: '#448AFF', fontWeight: 400, fontFamily: "'Patrick Hand', cursive" }}>Sign in</span>
            </button>
          )}
        </div>

      </div>
    </motion.header>
  );
};
